import React, { useEffect, useMemo, useRef, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import { Form } from 'react-bootstrap';
import dayjs from 'dayjs';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useLoading } from '../Utils/LoadingContext';
import EmployeeProcessService from '../services/EmployeeProcessService';
import LeaveProcessService from '../services/LeaveProcessService';
import './EmployeeAttendance.css';

const statusConfig = {
  PP: { bg: '#22c55e', label: 'Present', short: 'P' },
  OD: { bg: '#3b82f6', label: 'On Duty', short: 'OD' },
  AR: { bg: '#16a34a', label: 'Regularized', short: 'AR' },
  AP: { bg: '#f97316', label: 'Half Day', short: 'HD' },
  PA: { bg: '#f97316', label: 'Half Day', short: 'HD' },
  WO: { bg: '#cbd5e1', label: 'Week Off', short: 'WO' },
  HO: { bg: '#a855f7', label: 'Holiday', short: 'HO' },
  AA: { bg: '#ef4444', label: 'Absent', short: 'A' },
  PL: { bg: '#f59e0b', label: 'On Leave', short: 'PL' },
  CL: { bg: '#f59e0b', label: 'On Leave', short: 'CL' },
  ACL: { bg: '#f59e0b', label: 'On Leave', short: 'ACL' },
  CLA: { bg: '#f59e0b', label: 'On Leave', short: 'CLA' },
  PCL: { bg: '#f59e0b', label: 'On Leave', short: 'PCL' },
  CLP: { bg: '#f59e0b', label: 'On Leave', short: 'CLP' }
};

const leaveColorMap = {
  CL: '#3b82f6',
  PL: '#8b5cf6',
  SL: '#10b981',
  EL: '#f59e0b',
  LWP: '#ef4444'
};

const getLeaveColor = (leaveCode) => leaveColorMap[leaveCode] || '#0ea5e9';

const defaultWorkingHours = {
  expected: '00h 00m',
  actual: '00h 00m',
  deducted: '00h 00m'
};

const defaultAttendanceStats = {
  present: 0,
  paidLeave: 0,
  weeklyOff: 0,
  holidays: 0,
  absent: 0,
  unpaidLeave: 0,
  deductionLeave: 0,
  deductionPD: 0,
  arrears: 0,
  insentive: 0,
  totalPaidDays: 0,
  isSandwich: 0
};

const defaultEmployeeInfo = {
  shiftName: 'N/A',
  shiftTiming: 'N/A',
  department: 'N/A',
  plant: 'N/A',
  reportingTo: 'N/A'
};

const EmployeeAttendance = () => {
  const { setLoading } = useLoading();
  const requestIdRef = useRef(0);

  const employeeName = localStorage.getItem('username') || 'User';
  const employeeCode = localStorage.getItem('empcode') || '0';

  const [events, setEvents] = useState([]);
  const [month, setMonth] = useState(dayjs().format('YYYY-MM'));
  const [calendarKey, setCalendarKey] = useState(0);
  const [calendarDate, setCalendarDate] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
  const [leaveBalance, setLeaveBalance] = useState([]);
  const [workingHours, setWorkingHours] = useState(defaultWorkingHours);
  const [attendanceStats, setAttendanceStats] = useState(defaultAttendanceStats);
  const [employeeInfo, setEmployeeInfo] = useState(defaultEmployeeInfo);

  const monthOptions = useMemo(() => {
    const currentYear = dayjs().year();
    return Array.from({ length: 12 }, (_, index) => {
      const date = dayjs(`${currentYear}-${String(index + 1).padStart(2, '0')}-01`);
      return {
        value: date.format('YYYY-MM'),
        label: date.format('MMMM YYYY')
      };
    });
  }, []);

  const isLatestRequest = (requestId) => requestIdRef.current === requestId;

  const getLeaveMMYY = (monthValue) => {
    if (!monthValue) return '';
    const [year, mon] = monthValue.split('-');
    return `${mon}${year}`;
  };

  const formatHours = (value) => {
    const num = Number(value || 0);
    const hours = Math.floor(num);
    const mins = Math.round((num - hours) * 60);
    return `${String(hours).padStart(2, '0')}h ${String(mins).padStart(2, '0')}m`;
  };

  const resetAttendanceSection = () => {
    setWorkingHours(defaultWorkingHours);
    setAttendanceStats(defaultAttendanceStats);
  };

  const loadAttendanceDetails = async (empCode, monthValue, requestId) => {
    try {
      const formData = new FormData();
      formData.append('ManagerCode', empCode);
      formData.append('EmployeeCode', empCode);
      formData.append('LeaveMMYY', getLeaveMMYY(monthValue));
      formData.append('Type', 'GetEmployeeAttendanceDetails');

      const response = await LeaveProcessService.getEmployeeAttendanceDetails(formData);
      if (!isLatestRequest(requestId)) return;

      const data = response?.data?.[0];
      if (response?.success && data) {
        setAttendanceStats({
          present: data.present || 0,
          paidLeave: data.paidLeave || 0,
          weeklyOff: data.weeklyOff || 0,
          holidays: data.holidays || 0,
          absent: data.absent || 0,
          unpaidLeave: data.unpaidLeave || 0,
          deductionLeave: data.deductionLeave || 0,
          deductionPD: data.deductionPD || 0,
          arrears: data.arrears || 0,
          insentive: data.insentive || 0,
          totalPaidDays: data.totalPaidDays || 0,
          isSandwich: data.isSandwich || 0
        });

        setWorkingHours({
          expected: formatHours(data.expected || 0),
          actual: formatHours(data.actual || 0),
          deducted: formatHours(data.deducted || 0)
        });

        setEmployeeInfo((prev) => ({
          ...prev,
          shiftName: data.shiftName || prev.shiftName,
          department: data.departmentName || prev.department,
          plant: data.plantName || prev.plant,
          reportingTo: data.reportingTo || prev.reportingTo
        }));
      } else {
        resetAttendanceSection();
      }
    } catch (error) {
      if (!isLatestRequest(requestId)) return;
      resetAttendanceSection();
      console.error('Attendance details error:', error);
    }
  };

  const fetchPunchData = async (empCode, monthValue, requestId) => {
    try {
      const response = await EmployeeProcessService.getEmployeePunch(empCode, monthValue);
      if (!isLatestRequest(requestId)) return;

      if (!(response?.success && response?.data?.statusCode === 200)) {
        setEvents([]);
        setCalendarDate(dayjs(monthValue).startOf('month').format('YYYY-MM-DD'));
        setCalendarKey((prev) => prev + 1);
        return;
      }

      const rawData = response?.data?.result || [];

      const calendarEvents = rawData.map((item) => {
        const date = dayjs(item.punchDates).format('YYYY-MM-DD');
        const status = item.status || '';
        const config = statusConfig[status] || null;

        return {
          title: config?.label || 'No Data',
          date,
          backgroundColor: 'transparent',
          borderColor: 'transparent',
          textColor: '#0f172a',
          extendedProps: {
            status,
            hoursWorked: formatHours(item.hoursWorked || 0),
            badgeColor: config?.bg || '#e2e8f0',
            badgeText: config?.short || '',
            punchINTime: item.punchINTime,
            punchOUTTime: item.punchOUTTime,
            shiftName: item.shiftName
          }
        };
      });

      const firstRow = rawData[0] || {};
      setEmployeeInfo((prev) => ({
        ...prev,
        shiftName: firstRow.shiftName || prev.shiftName,
        department: firstRow.departmentName || prev.department,
        plant: firstRow.plantName || prev.plant,
        reportingTo: firstRow.reportingTo || prev.reportingTo
      }));

      setEvents(calendarEvents);
      setCalendarKey((prev) => prev + 1);
      setCalendarDate(dayjs(monthValue).startOf('month').format('YYYY-MM-DD'));
    } catch (error) {
      if (!isLatestRequest(requestId)) return;
      setEvents([]);
      setCalendarDate(dayjs(monthValue).startOf('month').format('YYYY-MM-DD'));
      console.error('Punch data error:', error);
    }
  };

  const loadShiftDetails = async (empCode, monthValue, requestId) => {
    try {
      const shiftData = new FormData();
      shiftData.append('ManagerCode', empCode);
      shiftData.append('EmployeeCode', empCode);
      shiftData.append('LeaveMMYY', getLeaveMMYY(monthValue));
      shiftData.append('Type', 'GetEmployeeShift');

      const response = await LeaveProcessService.getEmployeeShiftDetails(shiftData);
      if (!isLatestRequest(requestId)) return;

      const data = response?.data?.result?.[0] || response?.data?.[0] || response?.data;
      if (response?.success && data) {
        setEmployeeInfo((prev) => ({
          ...prev,
          shiftName: data.shiftName || prev.shiftName,
          shiftTiming:
            data.shiftStartTime || data.shiftEndTime
              ? `${data.shiftStartTime || '--'} - ${data.shiftEndTime || '--'}`
              : prev.shiftTiming,
          department: data.departmentName || prev.department,
          plant: data.plantName || prev.plant,
          reportingTo: data.reportingTo || prev.reportingTo
        }));
      }
    } catch (error) {
      if (!isLatestRequest(requestId)) return;
      console.error('Shift details error:', error);
    }
  };

  const loadLeaveBalance = async (empCode, monthValue, requestId) => {
    try {
      const formData = new FormData();
      formData.append('EmployeeCode', empCode);
      formData.append('Type', 'GetLeave');
      formData.append('LeaveMMYY', monthValue);

      const res = await LeaveProcessService.getEmployeeDashBoard(formData);
      if (!isLatestRequest(requestId)) return;

      const result = res?.data?.result || [];
      if (res?.data?.statusCode === 200 && Array.isArray(result)) {
        const mapped = result.map((x) => ({
          leaveCode: x.leaveCode || x.LeaveCode || x.leave_name || x.leaveType || 'N/A',
          totalBalance: x.availableBalance ?? x.AvailableBalance ?? x.balance ?? x.totalBalance ?? 0
        }));
        setLeaveBalance(mapped);
      } else {
        setLeaveBalance([]);
      }
    } catch (error) {
      if (!isLatestRequest(requestId)) return;
      setLeaveBalance([]);
      console.error('Leave balance error:', error);
    }
  };

  const loadAllData = async (empCode, monthValue) => {
    if (!empCode || empCode === '0' || !monthValue) return;

    const requestId = Date.now();
    requestIdRef.current = requestId;

    try {
      setLoading(true);
      await Promise.allSettled([
        fetchPunchData(empCode, monthValue, requestId),
        loadLeaveBalance(empCode, monthValue, requestId),
        loadAttendanceDetails(empCode, monthValue, requestId),
        loadShiftDetails(empCode, monthValue, requestId)
      ]);
    } finally {
      if (isLatestRequest(requestId)) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    loadAllData(employeeCode, month);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeCode, month]);

  const renderEventContent = (eventInfo) => {
    const { hoursWorked, badgeColor, badgeText, punchINTime, punchOUTTime } = eventInfo.event.extendedProps;

    return (
      <div className="attendance-day-content">
        <div className="attendance-day-label">Total Hours</div>
        <div className="attendance-day-hours">{hoursWorked}</div>
        <div className="attendance-day-times">
          <div className="attendance-day-time-row">
            <span className="attendance-time-value">
              {punchINTime || '--'} | {punchOUTTime || '--'}
            </span>
          </div>
        </div>
        {badgeText ? (
          <span className="attendance-day-badge" style={{ backgroundColor: badgeColor }}>
            {badgeText}
          </span>
        ) : null}
      </div>
    );
  };

  const recentPunchHistory = useMemo(() => {
    return [...events]
      .filter((item) => item.extendedProps?.punchINTime || item.extendedProps?.punchOUTTime)
      .sort((a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf())
      .slice(0, 5);
  }, [events]);

  const todayEvent = useMemo(() => {
    const today = dayjs().format('YYYY-MM-DD');
    return events.find((item) => item.date === today);
  }, [events]);

  const legendItems = [
    { short: 'P', text: 'Present', bg: '#22c55e' },
    { short: 'A', text: 'Absent', bg: '#ef4444' },
    { short: 'L', text: 'Late', bg: '#f97316' },
    { short: 'HD', text: 'Half Day', bg: '#ea580c' },
    { short: 'HO', text: 'Holiday', bg: '#a855f7' },
    { short: 'WO', text: 'Week Off', bg: '#e5e7eb', color: '#64748b' },
    { short: 'OD', text: 'On Duty', bg: '#3b82f6' },
    { short: 'CL', text: 'On Leave', bg: '#f59e0b' }
  ];

  return (
    <div className="attendance-dashboard-page">
      <div className="attendance-topbar">
        <div>
          <h4 className="attendance-page-title">My Attendance</h4>
          <div className="attendance-employee-text">
            {employeeName} ({employeeCode})
          </div>
        </div>

        <div className="attendance-filter-box">
          <Form.Label className="mb-1 small text-muted">Select Month</Form.Label>
          <Form.Select value={month} onChange={(e) => setMonth(e.target.value)}>
            {monthOptions.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </Form.Select>
        </div>
      </div>

      <div className="attendance-layout-grid">
        <div className="attendance-left-column">
          <div className="attendance-panel-card">
            <div className="attendance-panel-title">🏢 Shift Information</div>

            <div className="attendance-info-row">
              <span>Shift</span>
              <strong>{employeeInfo.shiftName}</strong>
            </div>
            <div className="attendance-info-row">
              <span>Timing</span>
              <strong>{employeeInfo.shiftTiming}</strong>
            </div>
            <div className="attendance-info-row">
              <span>Department</span>
              <strong>{employeeInfo.department}</strong>
            </div>
            <div className="attendance-info-row">
              <span>Plant</span>
              <strong>{employeeInfo.plant}</strong>
            </div>
            <div className="attendance-info-row">
              <span>Reporting To</span>
              <strong>{employeeInfo.reportingTo}</strong>
            </div>
          </div>

          <div className="attendance-panel-card">
            <div className="attendance-panel-title">🗓️ Today&apos;s Summary</div>

            <div className="attendance-info-row">
              <span>Status</span>
              <strong>{todayEvent?.title || 'Not Punched'}</strong>
            </div>
            <div className="attendance-info-row">
              <span>Punch In</span>
              <strong>{todayEvent?.extendedProps?.punchINTime || '—'}</strong>
            </div>
            <div className="attendance-info-row">
              <span>Punch Out</span>
              <strong>{todayEvent?.extendedProps?.punchOUTTime || '—'}</strong>
            </div>
            <div className="attendance-hours-strip">
              <div>
                <span>Expected</span>
                <strong>{workingHours.expected}</strong>
              </div>
              <div>
                <span>Actual</span>
                <strong>{workingHours.actual}</strong>
              </div>
              <div>
                <span>Deducted</span>
                <strong>{workingHours.deducted}</strong>
              </div>
            </div>
          </div>

          <div className="attendance-panel-card">
            <div className="attendance-calendar-header attendance-history-head">
              <h5>Leave Balance</h5>
              <span>{leaveBalance.length} items</span>
            </div>

            {leaveBalance.length > 0 ? (
              <div className="attendance-leave-grid attendance-leave-list">
                {leaveBalance.map((item, index) => (
                  <div
                    className="attendance-leave-card"
                    key={`${item.leaveCode}-${index}`}
                    style={{
                      borderLeft: `5px solid ${getLeaveColor(item.leaveCode)}`,
                      background: `linear-gradient(90deg, ${getLeaveColor(item.leaveCode)}15 0%, #f8fafc 35%)`
                    }}
                  >
                    <span>{item.leaveCode}</span>
                    <strong>{item.totalBalance}</strong>
                  </div>
                ))}
              </div>
            ) : (
              <div className="attendance-empty-text">No leave balance found.</div>
            )}
          </div>
        </div>

        <div className="attendance-right-column">
          <div className="attendance-panel-card attendance-calendar-card">
            <div className="attendance-calendar-header">
              <h5>Month&apos;s Attendance</h5>
              <span>{dayjs(month).format('MMMM YYYY')}</span>
            </div>

            <div className="attendance-calendar-scroll">
              <FullCalendar
                key={calendarKey}
                plugins={[dayGridPlugin]}
                initialView="dayGridMonth"
                initialDate={calendarDate}
                validRange={{
                  start: dayjs(calendarDate).startOf('month').format('YYYY-MM-DD'),
                  end: dayjs(calendarDate).endOf('month').add(1, 'day').format('YYYY-MM-DD')
                }}
                events={events}
                eventContent={renderEventContent}
                headerToolbar={false}
                fixedWeekCount={false}
                height="auto"
                contentHeight="auto"
                dayMaxEvents={1}
              />
            </div>

            <div className="attendance-legend-row">
              {legendItems.map((item) => (
                <div className="attendance-legend-item" key={item.text}>
                  <span
                    className="attendance-legend-badge"
                    style={{ backgroundColor: item.bg, color: item.color || '#fff' }}
                  >
                    {item.short}
                  </span>
                  <span className="attendance-legend-text">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="attendance-panel-card">
            <div className="attendance-calendar-header attendance-history-head">
              <h5>Recent Punch History</h5>
              <span>{recentPunchHistory.length} records</span>
            </div>

            {recentPunchHistory.length > 0 ? (
              <div className="attendance-history-list">
                {recentPunchHistory.map((item) => (
                  <div className="attendance-history-row" key={item.date}>
                    <div>
                      <div className="attendance-history-date">{dayjs(item.date).format('DD MMM YYYY')}</div>
                      <div className="attendance-history-status">{item.title}</div>
                    </div>
                    <div className="attendance-history-time">
                      {item.extendedProps?.punchINTime || '--'} - {item.extendedProps?.punchOUTTime || '--'}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="attendance-empty-text">No punch history found.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeAttendance;
